import {
  GeckoMeasurement,
  GeckoMeasurementJson,
  GeckoMeasurementUnit,
} from 'types'

/**
 * Pressure handling for the close-out rollup.
 *
 * Exists as its own module because `GeckoMeasurement.convertTo` is NOT safe for
 * blind comparison: `convertNumber` falls through to `return num` when the two
 * units are of different dimensions (and its `catch` does the same), so
 * converting a length to a pressure silently yields the raw number. Comparing
 * re-rated MAWPs that way could pick a "minimum" out of incompatible values, and
 * a derate pressure is a number an engineer acts on. Every value therefore has
 * its dimension validated before it is used.
 */

/**
 * The canonical unit every pressure is normalised to before comparison. MPa
 * matches `WORKING_UNIT_PRESS` in `FfsStrategyContext`, so the rollup speaks the
 * same unit as the calculations feeding it.
 */
export const CANONICAL_PRESSURE_UNIT = GeckoMeasurementUnit.Megapascals

/**
 * The complete set of pressure units the conversion table knows
 * (`conversions.ts` `toPressureUnit`). Anything else is not a pressure and must
 * not be converted — hence an explicit allowlist rather than a try/catch, which
 * would not fail on a dimension mismatch anyway.
 */
const PRESSURE_UNIT_IDS: ReadonlySet<string> = new Set([
  GeckoMeasurementUnit.PoundsForcePerSqInch.id,
  GeckoMeasurementUnit.Megapascals.id,
])

/**
 * The aggregate re-rate pressure for a scope.
 *
 * `unavailable` is a first-class outcome, not a null: if ANY derating LTA's
 * pressure is missing or unusable, reporting the minimum of the rest would
 * understate the derate, because the unknown value could be the governing one.
 */
export type DeratePressure =
  | { kind: 'known'; mawp: GeckoMeasurement }
  | { kind: 'unavailable' }

export function isPressure(
  measurement: GeckoMeasurement | null | undefined
): measurement is GeckoMeasurement {
  return Boolean(measurement && PRESSURE_UNIT_IDS.has(measurement.unit.id))
}

/**
 * A pressure's magnitude in the canonical unit, or `null` when the value is
 * absent, non-finite, or not a pressure at all.
 */
export function toCanonicalPressure(
  measurement: GeckoMeasurement | null | undefined
): number | null {
  if (!isPressure(measurement)) return null
  const value = measurement.convertTo(CANONICAL_PRESSURE_UNIT).num
  return Number.isFinite(value) ? value : null
}

/**
 * Relative epsilon, so two reports that computed the same MAWP through different
 * units are not treated as disagreeing over float noise. The absolute floor
 * keeps near-zero values from collapsing to "always equal".
 */
export function pressuresDisagree(
  a: GeckoMeasurement | null,
  b: GeckoMeasurement | null
): boolean {
  const left = toCanonicalPressure(a)
  const right = toCanonicalPressure(b)
  if (left === null && right === null) return false
  // One usable value and one not IS a disagreement: the reports do not describe
  // the same re-rate, and silently preferring the readable one would hide that.
  if (left === null || right === null) return true
  return (
    Math.abs(left - right) >
    Math.max(1e-9, 1e-6 * Math.max(Math.abs(left), Math.abs(right)))
  )
}

/**
 * The conservative aggregate across every derating LTA in a scope: the lowest
 * pressure, or `unavailable` the moment one input cannot be read.
 *
 * An EMPTY input is `unavailable` rather than `known`, so a caller that reaches
 * here with no derating LTAs cannot fabricate a pressure.
 */
export function conservativeMinPressure(
  pressures: ReadonlyArray<GeckoMeasurement | null>
): DeratePressure {
  if (pressures.length === 0) return { kind: 'unavailable' }

  let lowest: GeckoMeasurement | null = null
  let lowestCanonical = Number.POSITIVE_INFINITY

  for (const pressure of pressures) {
    const canonical = toCanonicalPressure(pressure)
    if (canonical === null) return { kind: 'unavailable' }
    if (canonical < lowestCanonical) {
      lowestCanonical = canonical
      lowest = pressure
    }
  }

  return lowest ? { kind: 'known', mawp: lowest } : { kind: 'unavailable' }
}

/** Persisted form of a measurement, in the canonical unit. */
export function pressureToJson(
  measurement: GeckoMeasurement
): GeckoMeasurementJson {
  const canonical = measurement.convertTo(CANONICAL_PRESSURE_UNIT)
  return { num: canonical.num, unit: canonical.unit.id }
}
